import type { OptionType } from "components/inputs";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import { useGetClientsQuery } from "services/client-api";
import { useGetFilesQuery } from "services/file-api";
import { useGetInvoicesQuery } from "services/invoice-api";

import type { ExtendedOptionType } from "../types";

export const useFinanceFilterData = () => {
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [fileSearchTerm, setFileSearchTerm] = useState("");
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");

  // create debounced setters
  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setClientSearchTerm(value);
      }, 300),
    [],
  );

  const debouncedSetFileSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setFileSearchTerm(value);
      }, 300),
    [],
  );

  const debouncedSetInvoiceSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setInvoiceSearchTerm(value);
      }, 300),
    [],
  );

  // fetch clients
  const { data: clientsData, isFetching: isClientsFetching } =
    useGetClientsQuery(
      {
        searchTerm: clientSearchTerm,
      },
      {
        skip: !clientSearchTerm || clientSearchTerm.length < 1,
      },
    );

  // fetch files
  const { data: filesData, isFetching: isFilesFetching } = useGetFilesQuery(
    {
      filters: {
        id: fileSearchTerm,
      },
    },
    {
      skip: !fileSearchTerm || fileSearchTerm.length < 1,
    },
  );

  // fetch invoices
  const { data: invoicesData, isFetching: isInvoicesFetching } =
    useGetInvoicesQuery(
      {},
      {
        skip: !invoiceSearchTerm || invoiceSearchTerm.length < 1,
      },
    );

  const clientOptions: ExtendedOptionType[] = useMemo(() => {
    if (!clientsData?.results) return [];
    return clientsData.results.map((client) => ({
      label: `${client.client_id} - ${client.name}`,
      value: client.id.toString(), // database ID for API
      clientId: client.client_id, // string identifier for display/URL
    }));
  }, [clientsData]);

  const fileOptions: OptionType[] = useMemo(() => {
    if (!filesData?.results) return [];
    return filesData.results.map((file) => ({
      label: `File ${file.id}`,
      value: file.id.toString(),
    }));
  }, [filesData]);

  const invoiceOptions: OptionType[] = useMemo(() => {
    if (!invoicesData || !Array.isArray(invoicesData)) {
      return [];
    }
    // filter invoices by search term on client side
    const filtered = invoicesData.filter((invoice) => {
      if (!invoice) return false;
      const invoiceNumber = invoice.quickbooks_invoice_number || "";
      return invoiceNumber
        .toLowerCase()
        .includes(invoiceSearchTerm.toLowerCase());
    });
    return filtered.map((invoice) => ({
      label: invoice.quickbooks_invoice_number || `Invoice ${invoice.id}`,
      value: invoice.quickbooks_invoice_number || invoice.id.toString(),
    }));
  }, [invoicesData, invoiceSearchTerm]);

  return {
    clientOptions,
    fileOptions,
    invoiceOptions,
    isClientsFetching,
    isFilesFetching,
    isInvoicesFetching,
    debouncedSetClientSearchTerm,
    debouncedSetFileSearchTerm,
    debouncedSetInvoiceSearchTerm,
  };
};
